import { inject, Injectable, type Injector, signal } from '@angular/core';

import { of, take } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import { DialogService, DialogWidth, ProtectedControlsService } from '@core/user-interface-legacy';
import { AskTemplatesDataService } from '@tellent-ask-hr/templates-logic';
import {
  AskTemplatesDialogComponent,
  type AskTemplatesDialogViews,
} from '@tellent-ask-hr/templates-smart-ui';
import type { AskPredefinedTemplate } from '@tellent-ask-hr/templates-types';

@Injectable({ providedIn: 'root' })
export class AskTemplatesDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly templatesDataService = inject(AskTemplatesDataService);
  private readonly protectedControls = inject(ProtectedControlsService);
  private readonly i18n = inject(I18nService);

  public openCreateTemplateDialog(
    injector: Injector,
    template: AskPredefinedTemplate | null,
  ): void {
    const templateCreated = signal(false);
    this.dialogService.openDialog(
      AskTemplatesDialogComponent,
      { firstView: 'form', templateCreated, template },
      {
        injector,
        width: DialogWidth.LargeX,
        closeGuard: () => {
          if (templateCreated() || !this.protectedControls.locked) return of(true);

          return this.dialogService.error({
            title: this.i18n.translate(
              'tlc.ask_hr.form_template_create.create_cancel_dialog.title',
            ),
            message: this.i18n.translate(
              'tlc.ask_hr.form_template_create.create_cancel_dialog.message',
            ),
            cancelLabel: this.i18n.translate('tlc.common.cancel'),
            agreeLabel: this.i18n.translate(
              'tlc.ask_hr.form_template_create.create_cancel_dialog.agree_label',
            ),
          });
        },
      },
    );
  }

  public openEditTemplateDialog(
    injector: Injector,
    options: { id: number; firstView: AskTemplatesDialogViews },
  ): void {
    this.templatesDataService
      .loadTemplateById(options.id)
      .pipe(take(1))
      .subscribe(({ isSuccess, success }) => {
        if (!isSuccess) return;

        this.dialogService.openDialog(
          AskTemplatesDialogComponent,
          { template: success!, firstView: options.firstView },
          {
            injector,
            width: DialogWidth.LargeX,
            closeGuard: () => {
              if (!this.protectedControls.locked) return of(true);

              return this.dialogService.error({
                title: this.i18n.translate(
                  'tlc.ask_hr.form_template_create.create_cancel_dialog.title',
                ),
                message: this.i18n.translate(
                  'tlc.ask_hr.form_template_create.create_cancel_dialog.message',
                ),
                cancelLabel: this.i18n.translate('tlc.common.cancel'),
                agreeLabel: this.i18n.translate(
                  'tlc.ask_hr.form_template_create.create_cancel_dialog.agree_label',
                ),
              });
            },
          },
        );
      });
  }
}
